import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import type { AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';

/** One ranked cost row — a department or a person — token spend plus allocated real cost. */
export interface CostRow {
    /** Raw group key: the department name, or a user id. */
    value: string;
    tokens: number;
    /** Allocated real spend ($); 0 when no cost data covers the row. */
    costUsd: number;
    turns: number;
    users: number;
    sessions: number;
    /** Token share of the (full, unlimited) total for this scope. */
    share: number;
}

/** A department row with its people nested beneath it, for the expandable cost tree. */
export interface DeptCostRow extends CostRow {
    people: CostRow[];
}

/**
 * Enough (department, person) pairs to cover the field at this scale. Every user
 * belongs to one department, so this bounds the number of people surfaced; if a
 * tenant ever exceeds it, the heaviest consumers still surface and department
 * totals are summed from what lands.
 */
const TREE_ROW_LIMIT = 500;

/**
 * Backs the Cost-by-Department page. A single `(user_department, user_id)`
 * aggregate is folded into a department→people tree: departments ranked by token
 * spend, each carrying its people as nested rows. Scoped by the page's ambient
 * assist filters, so it re-queries when filters change.
 */
@injectable()
export class TokenUsageCostDrilldownViewModel extends BaseViewModel {
    tree: DeptCostRow[] = [];
    totalTokens = 0;
    /** Allocated real spend ($) across the whole scope; 0 when no cost data has landed. */
    totalCostUsd = 0;
    loading = false;
    errorMessage: string | null = null;
    private key: string | null = null;

    constructor(@inject(IAnalyticsGatewayToken) private readonly gateway: IAnalyticsGateway) {
        super();
    }

    async loadTree(filters: AnalyticsFilter[]): Promise<void> {
        const key = `tree::${serializeFilters(filters)}`;
        if (this.key === key && (this.tree.length > 0 || this.loading)) return;
        this.key = key;
        this.tree = [];
        this.totalTokens = 0;
        this.totalCostUsd = 0;
        this.loading = true;
        this.errorMessage = null;
        this.notifyChange();
        try {
            const result = await this.gateway.aggregate({
                source: 'interactions',
                groupBy: ['user_department', 'user_id'],
                metrics: ['totalTokens', 'turns', 'users', 'sessions', 'allocatedCostUsd'],
                orderBy: { metric: 'totalTokens', direction: 'desc' },
                filters,
                limit: TREE_ROW_LIMIT,
            });
            const total = result.totals.totalTokens;
            this.totalTokens = total;
            this.totalCostUsd = result.totals.allocatedCostUsd;

            const byDept = new Map<string, DeptCostRow>();
            for (const row of result.rows) {
                const deptKey = row.keys['user_department'] ?? '—';
                const userKey = row.keys['user_id'] ?? '—';
                const tokens = row.metrics.totalTokens;
                const person: CostRow = {
                    value: userKey,
                    tokens,
                    costUsd: row.metrics.allocatedCostUsd,
                    turns: row.metrics.turns,
                    users: 1,
                    sessions: row.metrics.sessions,
                    share: total > 0 ? tokens / total : 0,
                };
                let dept = byDept.get(deptKey);
                if (dept === undefined) {
                    dept = { value: deptKey, tokens: 0, costUsd: 0, turns: 0, users: 0, sessions: 0, share: 0, people: [] };
                    byDept.set(deptKey, dept);
                }
                dept.people.push(person);
                dept.tokens += tokens;
                dept.costUsd += person.costUsd;
                dept.turns += person.turns;
                dept.sessions += person.sessions;
                dept.users += 1;
            }

            const tree = [...byDept.values()];
            for (const dept of tree) {
                dept.share = total > 0 ? dept.tokens / total : 0;
                dept.people.sort((a, b) => b.tokens - a.tokens);
            }
            tree.sort((a, b) => b.tokens - a.tokens);
            this.tree = tree;
        } catch (err) {
            this.errorMessage = err instanceof Error ? err.message : String(err);
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }
}

function serializeFilters(filters: AnalyticsFilter[]): string {
    return filters
        .map((filter) => `${filter.field}=${[...filter.values].sort().join('|')}`)
        .sort()
        .join(';');
}
